/**
 * 逸脱項目ビューで表セクションを扱うためのロジック。
 *
 * 逸脱項目側では表全体ではなく、逸脱セルを含む行だけに絞った表を出す。
 */
import {
  ReportNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { ReportNodesDict } from 'src/exShared/types/types'
import { getDescendantNodes } from 'src/exShared/util/nodes/getDescendantNodes'
import { isInvalid } from 'src/exShared/util/report/isInvalid'
import { isDefined } from 'src/util/isDefined'
import {
  isTableSectionNode,
  parseTableSection,
  TableSectionCell,
  TableSectionView,
} from './parseTableSection'

/**
 * 逸脱セルかどうか。非活性のセルは回答対象外なので逸脱に数えない。
 */
const isDeviatedCell = (cell: TableSectionCell): boolean =>
  cell.active && isInvalid(cell.questionNode.question)

/** 逸脱セルを含む行だけに絞った view を返す。逸脱項目ビューで使う。 */
const filterDeviatedRows = (view: TableSectionView): TableSectionView => ({
  ...view,
  rows: view.rows.filter(row => row.cells.some(isDeviatedCell)),
})

/** 逸脱項目ビューに表示する、逸脱行だけに絞った表セクション。 */
export type DeviatedTableSection = {
  sectionNode: ReportNodeSchema
  /** 逸脱セルを含む行だけに絞り込み済みの view */
  view: TableSectionView
}

/**
 * 指定ノード群に含まれる表セクションのうち、逸脱行を持つものだけを
 * 逸脱行に絞り込んだ view として返す。逸脱行が無い表は除外する。
 */
export const getDeviatedTableSections = (
  targetNodes: ReportNodeSchema[],
  allNodes: ReportNodesDict,
): DeviatedTableSection[] =>
  targetNodes
    .filter(isTableSectionNode)
    .map(sectionNode => {
      const parsed = parseTableSection(sectionNode, allNodes)
      if (parsed === undefined) return undefined
      const view = filterDeviatedRows(parsed)
      // 逸脱行が無い表は逸脱項目に出さない
      return view.rows.length > 0 ? { sectionNode, view } : undefined
    })
    .filter(isDefined)

/**
 * 表セクション配下にあるセル質問ノードの id 集合。
 * 表として表示するため、逸脱項目の一覧からこれらを除外するのに使う。
 */
export const collectTableCellNodeIds = (
  allNodes: ReportNodesDict,
): Set<number> => {
  const ids = new Set<number>()
  Object.values(allNodes)
    .filter(isTableSectionNode)
    .forEach(tableSection => {
      getDescendantNodes(tableSection.nodes ?? [], allNodes)
        .arrayOfNode.filter(node => node.type === TemplateNodeTypeEnum.Question)
        .forEach(node => ids.add(node.id))
    })
  return ids
}
